/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import DataGridV2, {
  DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { InfoBanner } from '@components/atoms/ui-kit/info-banner.tsx';
import { InfoTooltip } from '@components/atoms/ui-kit/info-tooltip.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { CustomModelGuideModal } from '@components/custom-models/custom-model-guide-modal.tsx';
import { usePageHeader } from '@utils/hooks/use-page-header';
import { useCreateCustomModel } from '@utils/api/custom-models/use-create-custom-models';
import { useDeleteCustomModel } from '@utils/api/custom-models/use-delete-custom-model.tsx';
import {
  useExportCustomModelDataset,
  downloadDatasetExport
} from '@utils/api/custom-models/use-export-dataset.tsx';
import {
  Model,
  useGetCustomModels
} from '@utils/api/custom-models/use-get-custom-models.tsx';
import {
  useGetModels,
  useGetTrainableModels
} from '@utils/api/models/use-get-models.ts';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { formatDate } from '@utils/date.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { Edit, HelpCircle, Plus, Trash2, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../router/paths.ts';
import { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

interface ModelRow {
  id: number;
  key: string;
  name: string;
  last_modified: string;
  organization_id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  language_data: any;
  is_trainable: boolean;
  type: number; // Changed from string to number to match API data
  base_language_id: number;
  base_language_key: string;
  base_language_key_normalized?: string;
  base_language_is_custom: boolean;
  training_status: string;
  training_status_badge_color: 'zinc' | 'amber' | 'sky' | 'emerald' | 'red';
}

export default function CustomModels() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelRow | undefined>();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState('');
  const [languageId, setLanguageId] = useState<string>('');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const { data: user } = useGetMe();

  const { data: supportedLanguages } = useGetModels();
  const { data: trainableLanguages } = useGetTrainableModels();
  const { data: organization } = useOrganization();
  const { data: modelsData, isLoading: modelIsLoading } = useGetCustomModels();
  const { mutate: createCustomModel, isLoading } = useCreateCustomModel();
  const { mutate: deleteCustomModel } = useDeleteCustomModel();
  const exportDataset = useExportCustomModelDataset();
  // Second stage of delete. The API refuses a model holding contributed
  // clips until the caller says it means to destroy them, because those
  // clips were cut from sessions by hand and the sources age out.
  const [contributionWarning, setContributionWarning] = useState<string>('');

  // Check if user has admin or sysop role
  const userRoles = (user?.result?.roles ?? []) as unknown as string[];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('sysop');

  // Show guide modal on first visit
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('customModelsGuideShown');
    if (!hasSeenGuide) {
      setShowGuideModal(true);
      try {
        localStorage.setItem('customModelsGuideShown', 'true');
      } catch {
        // noop
      }
    }
  }, []);

  // Transform data for DataGridV2
  const rows = useMemo<ModelRow[]>(() => {
    if (!modelsData) return [];

    const statusMapping: Record<number, string> = {
      1: t('not-running'),
      2: t('ready-to-run'),
      3: t('running'),
      4: t('success'),
      5: t('failed')
    };

    const statusColorMapping: Record<
      number,
      'zinc' | 'amber' | 'sky' | 'emerald' | 'red'
    > = {
      1: 'zinc',
      2: 'amber',
      3: 'sky',
      4: 'emerald',
      5: 'red'
    };

    const norm = (s?: string) => (s ?? '').toLowerCase().trim();
    const supported = supportedLanguages?.result ?? [];
    const knownKeys = new Set(supported.map(l => norm(l.key)));

    const extractKey = (raw?: string) => {
      if (!raw) return undefined;
      const tokens = raw
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter(Boolean);
      const hit = tokens.find(t => knownKeys.has(t));
      if (hit) return hit;
      return tokens.find(t => t.length === 2 || t.length === 3);
    };

    return modelsData.result.map((model: Model) => {
      const normalizedKey =
        extractKey(model.base_language_key) ??
        extractKey(model.key) ??
        undefined;

      const languageData = normalizedKey
        ? supported.find(l => norm(l.key) === normalizedKey)
        : undefined;

      // No match in the public language list → parent is a custom model.
      const baseLanguageIsCustom = !languageData;

      const fullName =
        languageData?.name ??
        normalizedKey ??
        model.base_language_key ??
        model.key ??
        '-';

      const rawStatus = model.training_log?.training_status;
      const trainingStatusNumber =
        typeof rawStatus === 'number' ? rawStatus : undefined;

      const trainingStatusText =
        trainingStatusNumber != null
          ? (statusMapping[trainingStatusNumber] ?? t('not_available'))
          : t('not_available');

      const trainingStatusBadgeColor =
        trainingStatusNumber != null
          ? (statusColorMapping[trainingStatusNumber] ?? 'zinc')
          : 'zinc';

      return {
        id: model.id,
        key: model.key,
        name: model.name,
        last_modified: formatDate(model.last_modified),
        organization_id: model.organization_id,
        language_data: model.language_data,
        is_trainable: model.is_trainable,
        type: model.type,
        base_language_id: model.base_language_id,
        base_language_key: fullName,
        base_language_key_normalized: normalizedKey,
        base_language_is_custom: baseLanguageIsCustom,
        training_status: trainingStatusText,
        training_status_badge_color: trainingStatusBadgeColor
      };
    });
  }, [modelsData, supportedLanguages, t]);

  // Define columns for DataGridV2
  const columns = useMemo<DataGridColumn<ModelRow>[]>(
    () => [
      {
        id: 'name',
        header: t('name'),
        field: 'name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'training_status',
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>{t('training-status')}</span>
            <InfoTooltip content={t('custom-model-tooltip-training-status')} />
          </div>
        ),
        field: 'training_status',
        maxWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'training_status',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('not-running'), value: '1' },
          { label: t('ready-to-run'), value: '2' },
          { label: t('running'), value: '3' },
          { label: t('success'), value: '4' },
          { label: t('failed'), value: '5' }
        ],
        cell: ({ row }) => (
          <Badge color={row.original.training_status_badge_color}>
            {row.original.training_status}
          </Badge>
        )
      },
      {
        id: 'base_language',
        accessorKey: 'base_language_key',
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>{t('base-language')}</span>
            <InfoTooltip content={t('custom-model-tooltip-base-language')} />
          </div>
        ),
        field: 'base_language_key',
        maxWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'base_language',
        serverOperator: 'ilike',
        cell: ({ row, getValue }) => (
          <div className="flex items-center gap-1.5">
            <span>{String(getValue() ?? '')}</span>
            {row.original.base_language_is_custom && (
              <Badge color="sky">
                {t('custom', { defaultValue: 'Custom' })}
              </Badge>
            )}
          </div>
        )
      },
      {
        id: 'last_modified',
        header: t('last-modified'),
        field: 'last_modified',
        maxWidth: 180,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  // Define actions for DataGridV2
  const actions = useMemo<GridAction<ModelRow>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-3.5 w-3.5" />,
        intent: 'primary',
        run: row => {
          navigate(PATHS(row.id.toString()).CUSTOM_MODEL);
        }
      },
      ...(isAdmin
        ? [
            {
              id: 'delete',
              label: t('delete'),
              icon: <Trash2 className="h-3.5 w-3.5" />,
              intent: 'danger' as const,
              run: (row: ModelRow) => {
                setSelectedModel(row);
                setDeleteModal(true);
              }
            }
          ]
        : [])
    ],
    [t, navigate, isAdmin]
  );

  const handleCellClick = (row: ModelRow, field: string) => {
    if (field === 'name' || field === 'base_language_key' || field === 'key') {
      navigate(PATHS(row.id.toString()).CUSTOM_MODEL);
    }
  };

  const handleExportDataset = () => {
    if (!selectedModel) return;
    exportDataset.mutate(selectedModel.id, {
      onSuccess: async data => {
        if (data.total === 0) {
          toast(t('dataset-export-empty'));
          return;
        }
        await downloadDatasetExport(data.files);
        toast.success(t('dataset-export-started', { count: data.total }));
      },
      onError: () => {
        toast.error(t('dataset-export-failed'));
      }
    });
  };

  const handleDeleteCustomModel = (acknowledgeContributions = false) => {
    if (!selectedModel) return;

    setIsDeleting(true);

    new Promise(resolve => setTimeout(resolve, 300)).then(() => {
      deleteCustomModel(
        { modelId: selectedModel.id, acknowledgeContributions },
        {
          onSuccess: () => {
            toast.success(t('custom-model-deleted-successfully'));
            setIsDeleting(false);
            setDeleteModal(false);
            setContributionWarning('');
            setSelectedModel(undefined);
          },
          onError: (error: AxiosError<BaseError>) => {
            const fallbackMessage = t(
              'an-error-occurred-while-deleting-custom-model'
            );
            const data = error?.response?.data;

            // Not a failure: the model holds contributed clips and the API is
            // asking whether we mean it. Keep the dialog open, say what will be
            // lost, and offer the export before the second press.
            if (data?.error === 'unacknowledged-contributions') {
              setContributionWarning(
                data.error_description || t('delete-destroys-contributions')
              );
              setIsDeleting(false);
              return;
            }

            let messages: string[] = [];

            if (Array.isArray(data?.detail)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              messages = data.detail.map((err: any) => {
                const field = err.loc?.[1] ?? 'field';
                return `${field}: ${err.msg}`;
              });
            } else if (data?.message) {
              messages = [data.message];
            } else if (data?.error_description) {
              messages = [data.error_description];
            } else {
              messages = [fallbackMessage];
            }

            toast.error(messages.join('\n'));
            setIsDeleting(false);
          }
        }
      );
    });
  };

  const handleCreate = () => {
    if (!organization || !organization.id) return;

    const payload = {
      name,
      language_id: Number(languageId),
      organization_id: Number(organization.id)
    };

    createCustomModel(payload, {
      onSuccess: () => {
        setIsModalOpen(false);
        setName('');
        setLanguageId('');
      }
    });
  };

  const languageOptions =
    trainableLanguages?.result?.map(item => ({
      label:
        item.type != null
          ? `${item.name} (${t('custom-model', { defaultValue: 'custom model' })})`
          : item.name,
      value: item.id.toString()
    })) ?? [];

  const FORM_ID = 'create-custom-model-form';

  const showEmptyState = rows.length === 0 && !modelIsLoading && !!modelsData;

  usePageHeader({
    title: t('custom-models'),
    description: t('create-custom-models'),
    actions: (
      <div className="flex items-center gap-2">
        <Button
          icon={<HelpCircle width={20} />}
          label={
            <span className="hidden sm:inline">{t('getting-started')}</span>
          }
          onClick={() => setShowGuideModal(true)}
          size="small"
          variant="smoke"
          title={t('getting-started')}
        />
        <Button
          icon={<Plus width={20} />}
          label={
            <span className="hidden sm:inline">{t('new-custom-model')}</span>
          }
          onClick={() => setIsModalOpen(true)}
          size="small"
          title={t('new-custom-model')}
        />
      </div>
    )
  });

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      <div className="w-full px-4 flex-shrink-0">
        <InfoBanner title={t('custom-model-info-banner-title')}>
          <p>{t('custom-model-what-are')}</p>
          <p>{t('custom-model-easy-with-data')}</p>
          <p>
            {t('custom-model-use-force-alignment')}{' '}
            <button
              onClick={() => navigate(PATHS().HOME)}
              className="underline font-medium text-start hover:opacity-80"
            >
              <Text as="span" tone="brand">
                {t('custom-model-use-force-alignment-link')}
              </Text>
            </button>
          </p>
          <p>{t('custom-model-improves-accuracy')}</p>
          <p className="font-medium">
            {t('custom-model-select-after-training')}
          </p>
        </InfoBanner>
      </div>

      <div className="w-full px-4 md:px-0 pb-4 sm:pb-0 relative flex-1 min-h-0 flex flex-col">
        <DataGridV2<ModelRow>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={modelsData ? modelsData.total_results : undefined}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={modelIsLoading || !modelsData}
          getRowId={row => String(row.id)}
          onCellClick={handleCellClick}
          cursor="pointer"
        />

        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <div className="h-16 w-16 rounded-full border-2 border-dashed border-hairline flex items-center justify-center">
                <Sparkles className="h-7 w-7" />
              </div>
              <Text as="p" size="sm" tone="muted">
                {t('no-custom-models-yet')}
              </Text>
              <Button
                label={t('create-first-custom-model')}
                icon={<Plus width={14} />}
                onClick={() => setIsModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setName('');
          setLanguageId('');
        }}
        size="small"
        title={t('new-custom-model')}
        footerActions={[
          <Button
            key="close"
            type="button"
            onClick={() => {
              setIsModalOpen(false);
              setName('');
              setLanguageId('');
            }}
            label={t('close')}
            size="small"
            variant="smoke"
          />,
          <Button
            key="create"
            type="submit"
            form={FORM_ID}
            label={t('create')}
            size="small"
            variant="primary"
            disabled={!name || !languageId || isLoading || !organization}
          />
        ]}
      >
        <form
          id={FORM_ID}
          onSubmit={e => {
            e.preventDefault();
            if (!isLoading && name && languageId && organization)
              handleCreate();
          }}
          className="space-y-4"
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <Input
            id="name"
            label={t('name')}
            placeholder={t('name')}
            onChange={e => setName(e.target.value)}
            type="text"
            value={name}
            autoComplete="given-name"
          />

          <div>
            <Text as="label" size="sm" weight="medium" className="block mb-2">
              {t('language')}
            </Text>
            <div className="h-10">
              <SelectInput
                label={null}
                onChange={(selected: { label: string; value: string }) => {
                  setLanguageId(selected?.value ?? '');
                }}
                value={
                  languageOptions.find(o => o.value === languageId) ?? null
                }
                options={languageOptions}
                isMulti={false}
                menuPlacement="bottom"
              />
            </div>
          </div>
        </form>
      </Modal>

      {selectedModel && (
        <ConfirmationModal
          isOpen={deleteModal}
          onClose={() => {
            setDeleteModal(false);
            setContributionWarning('');
            setSelectedModel(undefined);
          }}
          onConfirm={() =>
            handleDeleteCustomModel(Boolean(contributionWarning))
          }
          size="small"
          title={t('delete-confirmation')}
          caption={
            contributionWarning ||
            t('are-you-sure-you-want-to-delete-this-model')
          }
          confirmLabel={
            isDeleting
              ? t('deleting')
              : contributionWarning
                ? t('delete-anyway')
                : t('delete')
          }
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        >
          {contributionWarning && (
            <Button
              label={t('export-training-data')}
              onClick={handleExportDataset}
              loading={exportDataset.isLoading}
              variant="secondary"
              className="w-full justify-center"
            />
          )}
        </ConfirmationModal>
      )}

      <CustomModelGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
      />
    </div>
  );
}
